import { getEntry } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta, personJsonLd } from "../lib/seo.js";
import Picture, { PORTRAIT } from "../components/Picture/Picture.jsx";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Prose from "../components/Prose/Prose.jsx";
import styles from "./person.module.css";
import { PORTRAIT_VERSION } from "../lib/assetVersions.js";

export async function loader({ params }) {
  const person = getEntry("team", params.slug);
  if (!person) throw new Response("Not Found", { status: 404 });
  return { person };
}

// React Router 8.4 passes the route's loader result to meta() as `loaderData` (not `data`).
export function meta({ loaderData }) {
  if (!loaderData)
    return buildMeta({
      title: "Not found",
      description: "This person page does not exist.",
      path: "/about/team",
      noindex: true,
    });
  const { person } = loaderData;
  const path = `/about/team/${person.slug}`;
  return buildMeta({
    title: `${person.name}, ${person.role}`,
    description: person.description,
    path,
    image: person.photo ? `${person.photo}-800.jpg` : undefined,
    type: "profile",
    jsonLd: [
      personJsonLd(person, path),
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Team", path: "/about/team" },
        { name: person.name, path },
      ]),
    ],
  });
}

export default function Person({ loaderData }) {
  const { person } = loaderData;
  return (
    <section className="section">
      <div className={`container ${styles.layout}`}>
        <div className={styles.photoWrap}>
          {person.photo ? (
            <Picture
              src={person.photo}
              alt={person.imageAlt || `Portrait of ${person.name}`}
              {...PORTRAIT}
              sizes="20rem"
              priority
              version={PORTRAIT_VERSION}
            />
          ) : null}
        </div>
        <div className={styles.content}>
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Team", path: "/about/team" },
              { name: person.name, path: `/about/team/${person.slug}` },
            ]}
          />
          <p className="eyebrow" style={{ marginTop: "var(--sp-4)" }}>
            {person.role}
          </p>
          <h1>{person.name}</h1>
          <Prose html={person.html} className="person-bio" />
          {person.location || person.facts?.length ? (
            <ul className={styles.facts} aria-label={`About ${person.name.split(" ")[0]}`}>
              {person.location ? <li>Based in {person.location}</li> : null}
              {(person.facts || []).map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
          ) : null}
          {person.linkedin ? (
            <p style={{ marginTop: "var(--sp-6)" }}>
              <a
                className="btn btn-secondary"
                href={person.linkedin}
                rel="noopener noreferrer"
                target="_blank"
              >
                {person.name.split(" ")[0]} on LinkedIn
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
